import {
  Badge,
  Box,
  ChatBubble,
  ChatMessage,
  ChatMessageMeta,
  ChatMessageMetaItem,
  Separator,
} from "@twilio-paste/core";
import {
  ReduxMedia,
  ReduxMessage,
} from "../../store/reducers/messageListReducer";
import React, { ReactNode } from "react";
import { MAX_MESSAGE_LINE_WIDTH } from "../../constants";
import wrap from "word-wrap";
import { MessageStatus } from "./MessageStatus";
import { getMessageTime } from "../../utils/timestampUtils";
import { ReduxParticipant } from "../../store/reducers/participantsReducer";
import { NotificationsType } from "../../store/reducers/notificationsReducer";

const today = new Date().toDateString();

interface MessageItemProps {
  message: ReduxMessage;
  conversationAttachments: Record<string, Record<string, Blob>>;
  onDownloadAttachments: (message: ReduxMessage) => Promise<Error | undefined>;
  onFileOpen: (file: Blob, { filename }: ReduxMedia) => void;
  setImagePreview: (imagePreview: {
    message: ReduxMessage;
    file: Blob;
    sid: string;
  }) => void;
  getAuthorFriendlyName: (message: ReduxMessage) => string;
  participants: ReduxParticipant[];
  use24hTimeFormat: boolean;
  firstMessagePerDay: string[];
  addNotifications: (notification: NotificationsType) => void;
}

const MessageItem: React.FC<MessageItemProps> = (props: MessageItemProps) => {
  const { message, getAuthorFriendlyName, participants, firstMessagePerDay } =
    props;
  const messageImages: ReduxMedia[] = [];
  const messageFiles: ReduxMedia[] = [];
  const currentDateCreated = message.dateCreated ?? null;
  (message.attachedMedia || []).forEach((file) => {
    const { contentType } = file;
    if (contentType.includes("image")) {
      messageImages.push(file);
      return;
    }
    messageFiles.push(file);
  });

  const wrappedBody = wrap(message.body ?? "", {
    width: MAX_MESSAGE_LINE_WIDTH,
    indent: "",
    cut: true,
  });

  const isOutbound = message.author === localStorage.getItem("username");

  const MetaItemWithMargin: React.FC<{ children: ReactNode }> = (props) => (
    <ChatMessageMetaItem>
      <div style={{ marginTop: "5px" }}>{props.children}</div>
    </ChatMessageMetaItem>
  );

  let metaItems = [
    <MetaItemWithMargin key={1}>
      <MessageStatus message={message} channelParticipants={participants} />
    </MetaItemWithMargin>,
    <MetaItemWithMargin key={2}>
      {isOutbound
        ? `${getAuthorFriendlyName(message)}・${getMessageTime(message)}`
        : `${getMessageTime(message)}・${getAuthorFriendlyName(message)}`}
    </MetaItemWithMargin>,
  ];

  if (isOutbound) {
    metaItems = metaItems.reverse();
  }

  return (
    <div>
      {currentDateCreated && firstMessagePerDay.includes(message.sid) && (
        <>
          <Separator orientation="horizontal" verticalSpacing="space50" />
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="center"
            alignItems="center"
          >
            <Badge as="span" variant="neutral">
              {currentDateCreated.toDateString() === today
                ? "Hoy"
                : currentDateCreated.toLocaleDateString("es-ES", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
            </Badge>
          </Box>
        </>
      )}
      <ChatMessage
        variant={isOutbound ? "outbound" : "inbound"}
        key={`${message.sid}.message`}
      >
        <ChatBubble>{wrappedBody}</ChatBubble>
        <ChatMessageMeta
          aria-label={`escrito por ${getAuthorFriendlyName(message)}`}
        >
          {metaItems}
        </ChatMessageMeta>
      </ChatMessage>
    </div>
  );
};

export default MessageItem;
